import Game from '../../../Game';
import BaseUISystem from '../BaseUISystem';
import PIXI from 'pixi.js';
import { PopupID } from '../../../State/UI/PopupID';
import { DataConflictActions } from '../../../State/Game/DataConflictActions';
import GameObject, { GameObjectEvent } from '../../../GameObject/GameObject';
import { IPlayerDataLocation, PlayerDataLocation } from 'Utility/DataConflictsHelper';
import { Layout } from 'Utility/layout/createLayout';
import { DataResolutionPopupLayout } from 'Layouts/DataResolutionPopupLayout';
import RelativePositionComponent from 'Component/Render/RelativePosition';
import Point from 'Utility/Point';
import SpriteComponent from 'Component/Render/Sprite';
import { WorldDataHelper } from 'Utility/WorldDataHelper';
import BaseMenu from 'System/UI/Menu/BaseMenu';
import TextComponent from 'Component/Render/Text';
import { UIActions } from 'State/UI/UIActions';
import Renderable from 'Component/Renderable';
import { applyDefaultButtonAnimation } from 'System/UI/UIHelper';

const base_path_to_bgs = 'resource/graphics/saved_state_bgs/';

/**
 * Designed for use on native-only; will need tweaks for FBInstant
 */
export default class DataConflictsMenuHandler extends BaseMenu {
    private layout: any;
    private availDataSets: IPlayerDataLocation[];
    private chosenDataSet: IPlayerDataLocation;
    private selectScreen: boolean = false;

    constructor(uiSystem: BaseUISystem) {
        super(PopupID.DATA_CONFLICT_POPUP, uiSystem, false);
    }

    async preload(): Promise<void> {
        await super.preload();
        // Level sprite sheets
    }

    protected onCreate() {
        super.onCreate();

        this.layout = new DataResolutionPopupLayout();;
        this.layout.root.addComponent(RelativePositionComponent);
        this.layout.root.getComponent(RelativePositionComponent).relativePosition = new Point(0.5, 0.5);
        this.element.addChild(this.layout.root);

        this.layout.ChooseContainer.selectSaveBtn1.on(GameObjectEvent.SELECTED, () => this.selectDataSet(1));
        this.layout.ChooseContainer.selectSaveBtn2.on(GameObjectEvent.SELECTED, () => this.selectDataSet(2));
        this.layout.SelectContainer.CanceBtn.on(GameObjectEvent.SELECTED, () => this.toggleScreens());
        this.layout.SelectContainer.SelectedBtn.on(GameObjectEvent.SELECTED, () => this.confirmedSelectedDataSet());

        applyDefaultButtonAnimation(this.layout.ChooseContainer.selectSaveBtn1);
        applyDefaultButtonAnimation(this.layout.ChooseContainer.selectSaveBtn2);
        applyDefaultButtonAnimation(this.layout.SelectContainer.CanceBtn);
        applyDefaultButtonAnimation(this.layout.SelectContainer.SelectedBtn);
    }

    private confirmedSelectedDataSet(): void {
        DataConflictActions.resolveConflicts(this.chosenDataSet.playerData);
        // Save to Facebook/Firebase
        Game.sdk.save(this.chosenDataSet.playerData);
        Game.sdk.flushSaveData();
    }

    private selectDataSet(slot: 1 | 2) {
        this.chosenDataSet = this.availDataSets[slot-1];
        this.applyDatasetToSlot(this.chosenDataSet, 3, slot === 1)
        this.toggleScreens()
    }

    private toggleScreens() {
        this.selectScreen = !this.selectScreen;
        this.layout.ChooseWrap.getComponent(Renderable).container.visible = !this.selectScreen;
        this.layout.SelectWrap.getComponent(Renderable).container.visible = this.selectScreen;
    }

    async onShow(params: {
        dataSets: IPlayerDataLocation[]
    }) {
        super.onShow(params);
        this.availDataSets = params.dataSets;

        this.applyDatasetToSlot(this.availDataSets[0], 2, true);
        this.applyDatasetToSlot(this.availDataSets[1], 1);
    }

    private applyDatasetToSlot(dataset: IPlayerDataLocation, slot: 1 | 2 | 3, highlight:boolean = false): void {
        const { level, stars, coins } = this.getCompareValues(dataset);
        const levelNumber = level === 0 ? 1 : level;
        const layout = slot === 1 ? this.layout.ChooseContainer.SaveState1 :
            slot === 2 ? this.layout.ChooseContainer.SaveState2 :
                this.layout.SelectContainer.SelectedSave;

        layout.levelText.getComponent(TextComponent).text
            = Game.locale.getText('Level').replace("[x]", levelNumber.toString());
        layout.coinAmount.getComponent(TextComponent).text = coins.toString();
        layout.starAmount.getComponent(TextComponent).text = stars.toString();

        layout.descText.getComponent(TextComponent).text = this.getDataEndPointText(dataset.location);

        layout.frame.getComponent(SpriteComponent).texture = "savestate_frame_beige";
        layout.descText.getComponent(TextComponent).style.fill = "#bb3c2f";
        let bg: GameObject<PIXI.Sprite>;
        bg = this.layout.ChooseContainer.SaveState1BG;

        if(highlight) {
            layout.frame.getComponent(SpriteComponent).texture = "savestate_frame_green";
            layout.descText.getComponent(TextComponent).style.fill = "#406833";
            bg = this.layout.ChooseContainer.SaveState2BG;
        }

        const texName = this.getWorldBGTexture(levelNumber);
        Game.resource.addIfNeeded(texName, base_path_to_bgs + texName);
        Game.resource.load().then(() => { bg.getComponent(SpriteComponent).texture = texName; });
    }

    private getCompareValues(dataset: IPlayerDataLocation): { level:number, stars:number, coins:number } {
        const starDataParsed = JSON.parse(dataset.playerData.stars) as number[];
        return {
            level: starDataParsed.length,
            stars: starDataParsed.reduce((a: number, b: number) => a + b, 0),
            coins: dataset.playerData.coins,
        }
    }

    private getWorldBGTexture(_levelNum: number): string {
        return `map${WorldDataHelper.getWorldNumber(_levelNum)}.png`;
    }

    private getDataEndPointText(location: PlayerDataLocation) {
        return location === PlayerDataLocation.DEVICE ?
            `${Game.locale.getText('DeviceProgress')}:` :
            `${Game.locale.getText('CloudProgress')}:`;
    }

    onDestroy() {
        super.onDestroy();
        this.availDataSets = [];
    }
}
